import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Api from 'services/api';
import './InvoiceIssueReport.css';

const InvoiceIssueReport = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [phase, setPhase] = useState('loading'); // loading | form | success | error | already_reported | closed
  const [invoiceData, setInvoiceData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!token) {
      setErrorMsg('No token provided. Please use the link from your invoice email.');
      setPhase('error');
      return;
    }

    Api.getPublicInvoiceIssueByToken(token)
      .then((res) => {
        if (res?.status === 200) {
          const data = res.data?.data ?? res.data;
          setInvoiceData(data);
          if (data?.has_open_issue) {
            setPhase('already_reported');
          } else {
            setPhase('form');
          }
        } else if (res?.status === 410 || res?.data?.closed) {
          setPhase('closed');
        } else {
          setErrorMsg(res?.data?.message || res?.data?.error || 'This link is invalid or has expired.');
          setPhase('error');
        }
      })
      .catch((err) => {
        if (err?.response?.status === 410 || err?.response?.data?.closed) {
          setPhase('closed');
        } else {
          setErrorMsg('Unable to load invoice information. Please try again later.');
          setPhase('error');
        }
      });
  }, [token]);

  const validate = () => {
    const errors = {};
    if (!description.trim()) {
      errors.description = 'Please describe the issue.';
    } else if (description.trim().length < 5) {
      errors.description = 'Description must be at least 5 characters.';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    const payload = {
      description: description.trim(),
    };

    try {
      const res = await Api.submitPublicInvoiceIssue(token, payload);
      if (res?.status === 200 || res?.status === 201) {
        setPhase('success');
      } else if (res?.status === 409) {
        setPhase('already_reported');
      } else if (res?.status === 410 || res?.data?.closed) {
        setPhase('closed');
      } else if (res?.status === 422) {
        const backendErrors = res?.data?.errors ?? {};
        const mapped = {};
        if (backendErrors.description) mapped.description = backendErrors.description[0];
        setFieldErrors(mapped);
      } else {
        setErrorMsg(res?.data?.message || res?.data?.error || 'Something went wrong. Please try again.');
        setPhase('error');
      }
    } catch (err) {
      if (err?.response?.status === 410 || err?.response?.data?.closed) {
        setPhase('closed');
      } else {
        setErrorMsg('Unable to submit your report. Please try again later.');
        setPhase('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="main-freePage">
      <img src="/images/logo.png" alt="" />
      <div className="iir-card">
        <div className="appointment-header">
          <p>Invoice  <b className='font-semibold'># {invoiceData?.invoice_number}</b></p>
        </div>

        {phase === 'loading' && (
          <div className="iir-center">
            <div className="iir-spinner" />
            <p className="iir-hint">Loading invoice information&#8230;</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="iir-center">
            <div className="iir-status-icon iir-status-icon--error">&#x2715;</div>
            <h2 className="iir-heading">Link Invalid or Expired</h2>
            <p>{errorMsg}</p>
          </div>
        )}

        {phase === 'closed' && (
          <div className="iir-center">
            <div className="iir-status-icon iir-status-icon--warn">&#x2715;</div>
            <h2 className="iir-heading">Link No Longer Active</h2>
            <p className="iir-hint">
              This link is no longer active. The issue reported for this invoice has already been resolved or closed.
            </p>
          </div>
        )}

        {phase === 'already_reported' && (
          <div className="iir-center">
            <div className="iir-status-icon iir-status-icon--warn">!</div>
            <h2 className="iir-heading">Issue Already Reported</h2>
            <p className="iir-hint">
              An issue has already been submitted for invoice{' '}
              <strong>{invoiceData?.invoice_number}</strong>. Our team will review it and get back to you.
            </p>
            {invoiceData?.open_issue && (
              <div className="iir-open-issue-badge">
                <span className="iir-badge-label">Status:</span>
                <span className="iir-badge">{invoiceData.open_issue.status ?? 'Open'}</span>
              </div>
            )}
          </div>
        )}

        {phase === 'success' && (
          <div className="iir-center">
            <div className="iir-status-icon iir-status-icon--success">&#x2713;</div>
            <h2 className="iir-heading">Issue Reported Successfully</h2>
            <p>
              Thank you, <strong>{invoiceData?.client_name}</strong>! Your report for invoice{' '}
              <strong>{invoiceData?.invoice_number}</strong> has been submitted. Our team will review
              it and follow up with you shortly.
            </p>
          </div>
        )}

        {phase === 'form' && invoiceData && (
          <>
            <div className="iir-invoice-header">
              {/* <h2 className="iir-heading">Report an Invoice Issue</h2>
              <div className="iir-invoice-meta">
                <div className="iir-meta-item">
                  <span className="iir-meta-label">Client</span>
                  <span className="iir-meta-value">{invoiceData?.client_name}</span>
                </div>
                <div className="iir-meta-item">
                  <span className="iir-meta-label">Invoice #</span>
                  <span className="iir-meta-value">{invoiceData?.invoice_number}</span>
                </div>
                {invoiceData?.billing_date && (
                  <div className="iir-meta-item">
                    <span className="iir-meta-label">Date</span>
                    <span className="iir-meta-value">{invoiceData?.billing_date}</span>
                  </div>
                )}
                {invoiceData?.total_due_bill != null && (
                  <div className="iir-meta-item">
                    <span className="iir-meta-label">Total Due</span>
                    <span className="iir-meta-value iir-amount">
                      ${Number(invoiceData?.total_due_bill).toFixed(2)}
                    </span>
                  </div>
                )}
              </div> */}
              <p>
                Please describe the problem with this invoice. You do not need to
                log in &#8212; your report will be sent directly to our billing team.
              </p>
            </div>

            <form className="iir-form" onSubmit={handleSubmit} noValidate>
              <div className="step_cards">
                <div className="row-primary">
                  <h2>
                    Description <span className="iir-required">*</span>
                  </h2>

                  <div className="row-primary w-100">
                    <textarea
                      id="iir-description"
                      className="type_inp input-in"
                      rows={5}
                      placeholder="Please describe the issue in detail&#8230;"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={2000}
                    />
                    {fieldErrors.description && <p className="iir-error-msg">{fieldErrors.description}</p>}
                  </div>
                </div>
                
              </div>

              <div className="modal-footer">
                <button type="submit" className="main_button" disabled={submitting}>
                  {submitting ? 'Submitting\u2026' : 'Submit Issue Report'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceIssueReport;
